import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth, analytics, functions } from '../../../firebase';
import { httpsCallable } from 'firebase/functions';
// logEvent also feeds the first-party stream via the GA4 bridge;
// the signature and GA4 behaviour are unchanged.
import { logEvent } from '../../../analytics/ga4';
import { EyeIcon, EyeOffIcon } from '@heroicons/react/solid';
import PasswordRequirements from '../../../components/auth/PasswordRequirements';
import { validatePasswordString } from '../../../hooks/auth/usePasswordValidation';
import InfoTooltip from '../../../components/InfoTooltip';
import { getBackgroundGradient } from '../../../utils/environmentTheme';
import TermsPrivacyConsent from '../../../components/TermsPrivacyConsent';

function SetUpPassword() {
    const [searchParams] = useSearchParams();
    const [email, setEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [errorCode, setErrorCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true);
    const navigate = useNavigate();
  // Analytics consistency
  const [accountType] = useState('Provider');
  const [userType, setUserType] = useState('');

  // Error code mapping for Provider password setup (email link)
  // CP-001L: Invalid or missing setup link parameters
  // CP-001M: Passwords do not match
  // CP-001P: Password policy/validation failure
  // CP-001N: Account not found
  // CP-001D: Permission denied for this setup process
  // CP-001A: Account already set up
  // CP-001V: Email verification required before setup
  // CP-001X: Setup link expired
  // CP-001T: Technical/internal error
  // CP-001G: Generic/uncategorized error

    // Password visibility states
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Validation states
    const [isPasswordValid, setIsPasswordValid] = useState(false);
    const [passwordMatch, setPasswordMatch] = useState(null);

  // Ensure consistent vertical spacing at the top of the panel:
  // in dev/staging we render an environment banner that provides top space;
  // in production (no banner), we add top margin to the logo so it doesn't
  // touch the rounded top edge of the card.
  const showEnvBanner = (import.meta.env.VITE_ENVIRONMENT === "development" || import.meta.env.VITE_ENVIRONMENT === "staging");

    const handleNewPasswordChange = (e) => {
        const newPass = e.target.value;
        setNewPassword(newPass);

        // Live update password match state if confirm password is already entered
        if (confirmPassword) {
            setPasswordMatch(confirmPassword === newPass);
        }
    };

    const handleConfirmPasswordChange = (e) => {
        const confirmPass = e.target.value;
        setConfirmPassword(confirmPass);
        setPasswordMatch(confirmPass === newPassword);
    };

    // Initialize component with URL parameter validation
    useEffect(() => {
        const initializeSetup = async () => {
            console.log('🔧 SetUpPassword: Initializing password setup flow');

            const encodedEmail = searchParams.get('e');
            const isSetup = searchParams.get('setup') === 'true';

            console.log('🔧 SetUpPassword: URL params -', {
                hasEncodedEmail: !!encodedEmail,
                isSetup
            });

            if (!encodedEmail) {
                console.error('❌ SetUpPassword: Missing encoded email parameter');
                setError('Invalid setup link. Please contact your organisation administrator.');
                setErrorCode('CP-001L'); // invalid or missing link param
                logEvent(analytics, 'password_setup_invalid_link', {
                    error: 'missing_encoded_email'
                });
                setIsInitializing(false);
                return;
            }

            if (!isSetup) {
                console.error('❌ SetUpPassword: Missing setup parameter');
                setError('Invalid setup link. Please contact your organisation administrator.');
                setErrorCode('CP-001L');
                logEvent(analytics, 'password_setup_invalid_link', {
                    error: 'missing_setup_param'
                });
                setIsInitializing(false);
                return;
            }

            try {
                const decodedEmail = atob(encodedEmail);
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

                console.log('🔧 SetUpPassword: Decoded email successfully');

                if (!emailRegex.test(decodedEmail)) {
                    throw new Error('Invalid email format');
                }

                setEmail(decodedEmail);
                console.log('✅ SetUpPassword: Email validation successful');

                // Upfront server-side check: verify the account hasn't already
                // been set up before showing the password form.  Reuses the
                // existing validation CF which checks Auth claims
                // (hasSetOwnPassword) without exposing any new information —
                // the email is already in the URL.  If the account is already
                // configured we redirect immediately, avoiding the user filling
                // in a form only to be told their account is already active.
                try {
                    const validateSetup = httpsCallable(functions, 'validatePasswordSetupRequest');
                    await validateSetup({ email: decodedEmail });
                    console.log('✅ SetUpPassword: Account eligible for password setup');
                } catch (validationError) {
                    const code = validationError?.code || '';
                    const msg = validationError?.message || '';
                    console.log('🔧 SetUpPassword: Validation result -', code, msg);

                    if (code === 'functions/failed-precondition' && msg.toLowerCase().includes('already')) {
                        console.log('ℹ️ SetUpPassword: Account already set up — redirecting to login');
                        logEvent(analytics, 'clinician_password_setup_already_complete', { accountType, userType });
                        navigate('/login?message=account_already_setup');
                        return;
                    }
                    if (code === 'functions/not-found') {
                        console.log('❌ SetUpPassword: Account not found — showing error screen');
                        setError('Account not found. Please contact your organisation administrator for assistance.');
                        setErrorCode('CP-001N');
                        logEvent(analytics, 'clinician_password_setup_account_not_found', { accountType, userType });
                        setIsInitializing(false);
                        return;
                    }

                    // For any other validation error (permission-denied, network failure,
                    // etc.) let the user proceed to the form — the submit-time check in
                    // completeProviderUserPasswordSetup provides the safety net.
                    console.warn('⚠️ SetUpPassword: Pre-check returned non-fatal error, proceeding to form:', code, msg);
                }

            } catch (error) {
                console.error('❌ SetUpPassword: Error decoding email:', error);
                setError('Invalid setup link. Please contact your organisation administrator.');
                setErrorCode('CP-001L');
                logEvent(analytics, 'password_setup_invalid_link', {
                    error_type: error.name
                });
            } finally {
                setIsInitializing(false);
            }
        };

        initializeSetup();
    }, [searchParams, navigate, accountType, userType]);

  // Fetch userType best-effort for analytics
  useEffect(() => {
    try {
      if (auth?.currentUser?.getIdTokenResult) {
        auth.currentUser.getIdTokenResult(true).then((res) => {
          try { if (res?.claims?.userType) setUserType(res.claims.userType); } catch (_) {}
        }).catch(() => {});
      }
    } catch (_) {}
  }, []);

    const handleSetupPassword = async (e) => {
        e.preventDefault();
        setError('');
    setErrorCode('');
        setIsLoading(true);

        console.log('🔧 SetUpPassword: Starting password setup process');

        // Comprehensive validation
        if (newPassword !== confirmPassword) {
            console.log('❌ SetUpPassword: Password mismatch');
            setError("Passwords do not match");
            setErrorCode('CP-001M');
            setIsLoading(false);
            return;
        }

        try {
            const { errors } = validatePasswordString(newPassword);
            if (errors.length > 0) {
                console.log('❌ SetUpPassword: Password validation failed:', errors);
                setError(errors.join('. '));
                setErrorCode('CP-001P');
                setIsLoading(false);
                return;
            }

            console.log('✅ SetUpPassword: Password validation passed');

            // Call new cloud function to complete password setup
            console.log('🔧 SetUpPassword: Calling completeProviderUserPasswordSetup function...');
            const completeProviderUserPasswordSetup = httpsCallable(functions, 'completeProviderUserPasswordSetup');
            const result = await completeProviderUserPasswordSetup({ 
                email: email,
                newPassword: newPassword 
            });

            console.log('✅ SetUpPassword: Cloud function call successful -', result.data);

            logEvent(analytics, 'clinician_password_setup_success', { accountType, userType });

            // Redirect to login page with success message
            console.log('🔧 SetUpPassword: Redirecting to login page...');
            try {
                const e = btoa(email);
                navigate('/login?message=setup_complete&e=' + encodeURIComponent(e));
            } catch (_) {
                // Fallback to url-encoded email if base64 fails
                navigate('/login?message=setup_complete&email=' + encodeURIComponent(email));
            }

        } catch (error) {
            console.error("❌ SetUpPassword: Setup error:", error);
            console.error("❌ SetUpPassword: Error code:", error.code);
            console.error("❌ SetUpPassword: Error message:", error.message);

            let errorMessage = "An error occurred. Please try again.";

            // Handle cloud function errors
            if (error.code === 'functions/invalid-argument') {
                if (error.message.includes('Password must')) {
                    errorMessage = error.message;
                    setErrorCode('CP-001P');
                } else {
                    errorMessage = "Please check your password requirements and try again.";
                    setErrorCode('CP-001P');
                }
            } else if (error.code === 'functions/not-found') {
                errorMessage = "Account not found. Please contact your organisation administrator.";
                setErrorCode('CP-001N');
            } else if (error.code === 'functions/permission-denied') {
                errorMessage = "Invalid account type for this setup process.";
                setErrorCode('CP-001D');
            } else if (error.code === 'functions/failed-precondition') {
                if (error.message.includes('Account already set up')) {
                    errorMessage = "Account already set up. Please sign in with your password.";
                    setErrorCode('CP-001A');
                    setTimeout(() => navigate('/login?message=account_already_setup'), 2000);
                } else if (error.message.includes('Email verification required')) {
                    errorMessage = "Email verification required. Please contact your organisation administrator.";
                    setErrorCode('CP-001V');
                } else if (error.message.includes('Setup link has expired')) {
                    errorMessage = "Setup link has expired. Please contact your organisation administrator for a new invitation.";
                    setErrorCode('CP-001X');
                } else {
                    errorMessage = error.message;
                    setErrorCode('CP-001G');
                }
            } else if (error.code === 'functions/internal') {
                errorMessage = "A technical error occurred. Please try again or contact your organisation administrator.";
                setErrorCode('CP-001T');
            } else {
                // Handle validation errors from our local validation
                if (error.message.includes('Password must')) {
                    errorMessage = error.message;
                    setErrorCode('CP-001P');
                }
            }

            setError(errorMessage);
            logEvent(analytics, 'clinician_password_setup_error', { error_code: error.code, accountType, userType });

        } finally {
            console.log('🔧 SetUpPassword: Password setup process completed');
            setIsLoading(false);
        }
    };

    // Show loading state during initialization
    if (isInitializing) {
        return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundImage: getBackgroundGradient() }}>
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl drop-shadow-lg px-8 py-10 mx-4 text-center">
                    <img src="/td_logo_full_2025.png" alt="Logo" className="mx-auto mt-10 mb-8" style={{ width: '220px', height: 'auto' }} />
                    <h2 className="text-2xl font-bold mb-2">Initialising...</h2>
                    <p className="text-gray-600 mb-6">Please wait while we verify your setup link.</p>
                    <div className="animate-spin mx-auto h-8 w-8 border-4 border-[#43213e] border-t-transparent rounded-full"></div>
                </div>
            </div>
        );
    }

    // Terminal error screen — shown when the setup link is invalid or the
    // account no longer exists. Replaces the password form entirely so the
    // user isn't confused by a form they can never successfully submit.
    const terminalErrorCodes = ['CP-001L', 'CP-001N'];
    if (error && terminalErrorCodes.includes(errorCode)) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundImage: getBackgroundGradient() }}>
                <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl drop-shadow-lg px-8 py-10 mx-4 text-center">
                    <img src="/td_logo_full_2025.png" alt="Logo" className="mx-auto mt-10 mb-8" style={{ width: '220px', height: 'auto' }} />

                    <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                        <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>

                    <h2 className="text-2xl font-bold mb-2">Unable to Set Up Account</h2>
                    <p className="text-gray-600 mb-2">{error}</p>
                    <p className="text-xs text-gray-400 mb-6">Code: {errorCode}</p>

                    <div className="space-y-3">
                        <button
                            onClick={() => navigate('/login')}
                            className="w-full font-semibold py-2 rounded-lg bg-primary text-white hover:bg-primary/90 transition duration-100"
                        >
                            Go to Sign In
                        </button>
                        <p className="text-xs text-gray-500">
                            If you believe this is an error, please contact your organisation administrator.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundImage: getBackgroundGradient() }}>
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl drop-shadow-lg px-8 pb-8 mx-4">
                <div className="w-full">
                    {showEnvBanner && (
                        <div className={`-mx-8 mb-10 px-8 py-3 border font-semibold text-center rounded-t-2xl
                          ${import.meta.env.VITE_ENVIRONMENT === "development" ? "bg-yellow-200 text-yellow-900 border-yellow-300" : "bg-blue-200 text-blue-900 border-blue-300"}
                        `}>
                            <div className="flex items-center justify-center gap-2">                
                                <span>
                                    {import.meta.env.VITE_ENVIRONMENT === "development" ? "Development Environment" : "Testing / Training Environment"}
                                </span>
                            </div>
                        </div>
                    )}

                    <img src="/td_logo_full_2025.png" alt="Logo" className={`mx-auto mb-8 ${!showEnvBanner ? 'mt-10' : ''}`} style={{ width: '220px', height: 'auto' }} />
                    <h2 className="text-2xl font-bold mb-2 text-center">Set Up Your Account</h2>
                    <p className="text-gray-600 mb-6 text-center">Please create a secure password for your account.</p>

                    <form onSubmit={handleSetupPassword}
                    // autoComplete="on"
                    >
                        <div className="mb-4">
                            <label className="block text-xs font-bold text-gray-700 mb-1 text-left">Email</label>
                            <input
                                type="email"
                                value={email}
                                readOnly
                                // autoComplete="username"
                                className="w-full px-4 py-2 border rounded-md bg-gray-50 text-gray-600 focus:outline-none placeholder:text-sm"
                            />
                        </div>

                        <div className="mb-4">
                            <div className="flex justify-between items-center">
                                <label className="block text-xs font-bold text-gray-700 mb-1 text-left">New Password</label>
                                <InfoTooltip
                                    side="right"
                                    content={"Your password needs to have 12 characters or more, and contain at least 1 number, 1 symbol and 1 upper & lower case letter"}
                                />
                            </div>
                            <div className="relative">
                                <input
                                    type={showNewPassword ? "text" : "password"}
                                    value={newPassword}
                                    onChange={handleNewPasswordChange}
                                    required
                                    placeholder="Enter your secure password"
                                    // autoComplete="new-password"
                                    className="w-full px-4 pr-10 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                                >
                                    <span className="relative h-5 w-5">
                                        <EyeIcon
                                            className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
                                                showNewPassword ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                                            }`}
                                        />
                                        <EyeOffIcon
                                            className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
                                                showNewPassword ? 'opacity-0 scale-75' : 'opacity-100 scale-100'
                                            }`}
                                        />
                                    </span>
                                </button>
                            </div>
                            <PasswordRequirements value={newPassword} onValidityChange={setIsPasswordValid} className="mt-2" />
                        </div>

                        <div className="mb-6">
                            <label className="block text-xs font-bold text-gray-700 mb-1 text-left">Confirm Password</label>
                            <div className="relative">
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    value={confirmPassword}
                                    onChange={handleConfirmPasswordChange}
                                    required
                                    placeholder="Confirm your password"
                                    // autoComplete="new-password"
                                    className="w-full px-4 pr-10 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm"
                                    disabled={!newPassword || !isPasswordValid}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    disabled={!newPassword || !isPasswordValid}
                                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                                >
                                    <span className="relative h-5 w-5">
                                        <EyeIcon
                                            className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
                                                showConfirmPassword ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                                            }`}
                                        />
                                        <EyeOffIcon
                                            className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
                                                showConfirmPassword ? 'opacity-0 scale-75' : 'opacity-100 scale-100'
                                            }`}
                                        />
                                    </span>
                                </button>
                            </div>
                            <div className={`mt-1 overflow-hidden transition-all duration-300 ${passwordMatch !== null ? 'max-h-6' : 'max-h-0'}`}>
                                <div className="flex items-start text-sm">
                                  <span className={`mr-2 font-bold ${passwordMatch ? 'text-green-600' : 'text-gray-500'}`} aria-hidden>
                                    {passwordMatch ? '✔' : '✘'}
                                  </span>
                                  <span className={`${passwordMatch ? 'text-green-700' : 'text-gray-700'}`}>
                                    {passwordMatch ? 'Passwords match' : 'Passwords do not match'}
                                  </span>
                                </div>
                            </div>
                        </div>

                        {/* Consolidated error banner below inputs and above CTA */}
                        <div className={`transition-all duration-300 overflow-hidden ${error ? 'max-h-24 mt-2 mb-2' : 'max-h-0 mt-0 mb-0'}`} role="alert">
                            <div className={`bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm rounded-lg relative ${error ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
                                <div className="flex items-start">
                                    <svg className="h-4 w-4 text-red-500 mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>{error}{errorCode ? ` (Code: ${errorCode})` : ''}</span>
                                </div>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading || !isPasswordValid || !passwordMatch}
                            className={`w-full font-semibold py-2 rounded-lg transition duration-100 mb-4 ${isLoading || !isPasswordValid || !passwordMatch
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-primary text-white hover:bg-primary/90'
                                }`}
                        >
                            {isLoading ? (
                                <div className="flex items-center justify-center">
                                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                    Setting up your account...
                                </div>
                            ) : (
                                'Set Up Password'
                            )}
                        </button>
                        <TermsPrivacyConsent />
                    </form>

                    

                    <div className="mt-6 text-center">
                        {(() => {
                          const subject = 'Reporting Issue with ThinkDivergent - Set Up Password';
                          const now = new Date();
                          const day = now.getDate();
                          const sfx = (d) => {
                            if (d % 100 >= 11 && d % 100 <= 13) return 'th';
                            switch (d % 10) { case 1: return 'st'; case 2: return 'nd'; case 3: return 'rd'; default: return 'th'; }
                          };
                          const easyTime = `${day}${sfx(day)} ${now.toLocaleString('en-GB', { month: 'long' })} ${now.getFullYear()} at ${now.toLocaleString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
                          const isoTime = now.toISOString();
                          const userId = auth?.currentUser?.uid || '';
                          let organisation = '';
                          // best-effort include organisation claim synchronously if available later
                          try { auth?.currentUser?.getIdTokenResult?.(false).then(res => { organisation = res?.claims?.organisation || ''; }); } catch (_) {}
                          const lines = [
                            'To ThinkDivergent Support Team,',
                            '',
                            'I would like to report an issue on the platform.',
                            '',
                            '[Please describe briefly what you were doing and what happened]',
                            '',
                            'Helpful Information:',
                            `- Screen: ${typeof window !== 'undefined' ? window.location.pathname : ''}`,
                            `- URL: ${typeof window !== 'undefined' ? window.location.href : ''}`,                            
                            `- Time: ${easyTime} (${isoTime})`,
                            userId ? `- User ID: ${userId}` : undefined,
                            organisation ? `- Organisation: ${organisation}` : undefined,
                            error ? `- Message: ${error}` : undefined,
                            `- Browser: ${typeof navigator !== 'undefined' ? navigator.userAgent : ''}`
                          ].filter(v => v != null).join('\n');
                          const href = `mailto:support@thinkdivergent.co.uk?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines)}`;
                          return (
                            <p className="text-xs text-gray-500">
                              Need help? Contact your organisation administrator or {""}
                              <a href={href} className="underline text-primary">report an issue</a>.
                            </p>
                          );
                        })()}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SetUpPassword;